import {
    applyVisibility,
    type BindableElementProps,
    hide,
    jsx,
    onConnect,
    show,
} from '@amazon/vinyl-tsx'
import {
    asData,
    data,
    type MaybeObservableValue,
    type MutableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import { ctx } from '@/util/context'
import {
    createDisposer,
    debounce,
    type Maybe,
    type MaybePromise,
    throttle,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { omit } from '@/util/objectUtil'
import { getScrollParent, getViewportRect, onResize, toLocalRect } from '@/util/domUtil'
import { IndexedPool } from '@/util/recycle/IndexedPool'
import { windowEvents } from '@/util/interaction'
import { intersectRects } from '@/util/geomUtil'
import { type ListController, ListControllerImpl } from '@/components/ListController'

export interface VirtualListProps<E> extends BindableElementProps<'div'> {
    /**
     * Total number of data elements.
     */
    count: MaybeObservableValue<Maybe<number>>

    /**
     * Provides the data item for the given index.
     * Response may be asynchronous.
     * VirtualList does not cache responses.
     */
    dataProvider: (index: number) => MaybePromise<E>

    /**
     * A factory for creating new html elements.
     * Expected to respond to data changes.
     */
    itemRenderer: (data$: MutableValue<E | null>, index$: ObservableValue<number>) => HTMLElement

    direction?: 'vertical' | 'horizontal'

    /**
     * If true (default), elements are assumed to be a fixed size.
     * (Affects max scroll position)
     */
    variableSizes?: boolean

    /**
     * Displayed when [count] is nullish.
     */
    loadingIndicator?: HTMLElement

    /**
     * The number of pixels to extend beyond the visible bounds for data-populated item renderers.
     * Default: 600
     */
    margin?: number

    /**
     * Sets a callback which provides this list's controller. This can be used
     * to listen for data changes and invalidate entries.
     * This will be called when the component is connected, and the returned
     * Unsubscribe will be called when disconnected.
     */
    useController?: (controller: ListController<E>) => Unsubscribe

    /**
     * The number of seconds to throttle calls to the data provider.
     */
    dataProviderThrottle?: number
}

/**
 * Default number of pixels to extend visible range for item renderers.
 */
const DEFAULT_MARGIN = 600

/**
 * A renderer with its data controller and index.
 */
interface Renderer<E> {
    readonly element: HTMLElement
    readonly data$: MutableValue<E | null>
    readonly index$: MutableValue<number>

    /**
     * Modification count.
     */
    mod: number

    /**
     * Hides the element if visible.
     */
    hide(): void

    /**
     * Shows the element if visible.
     */
    show(): void
}

export function VirtualList<E>(props: VirtualListProps<E>) {
    const { errorHandler } = ctx('VirtualList')
    const { dataProvider, itemRenderer, direction, variableSizes, count } = props
    if (variableSizes !== false) throw new Error('variableSizes not currently supported.')

    const controller = new ListControllerImpl<E>()

    const view = (
        <div
            {...omit(props, ['dataProvider', 'itemRenderer', 'direction', 'variableSizes'])}
            className={`virtualList ${props.className ?? ''} ${direction}`}
        >
            {props.loadingIndicator}
        </div>
    )

    const countData = asData(count)
    if (props.loadingIndicator) {
        applyVisibility(
            props.loadingIndicator,
            countData.map((value) => value == null)
        )
    }

    // Distinguishes an internal update from an update done by the renderer.
    let internalUpdate = 0

    function createRenderer(): Renderer<E> {
        const data$ = data<E | null>(null)
        const index$ = data(-1)
        data$.onData((current, previous) => {
            if (current != null && !internalUpdate) {
                controller.notifyChanged({
                    index: index$.value,
                    current,
                    previous: previous ?? null,
                })
            }
        })
        const element = itemRenderer(data$, index$)
        element.classList.add('renderer')
        let visible = false
        hide(element)
        view.append(element)
        return {
            element,
            data$,
            index$,
            mod: 0,
            hide() {
                if (!visible) return
                hide(element)
                visible = false
            },
            show() {
                if (visible) return
                show(element)
                visible = true
            },
        }
    }

    function clearRenderer(renderer: Renderer<E>, index: number) {
        const active = document.activeElement
        if (active && renderer.element.contains(active)) {
            ;(active as HTMLElement).blur()
        }
        renderer.index$.value = index
        internalUpdate++
        renderer.data$.value = null
        internalUpdate--
    }

    function refreshRendererData(renderer: Renderer<E>, index: number, forceInvalidate: boolean) {
        const n = countData.value ?? 0
        if (index < 0 || index >= n) return
        const mod = ++renderer.mod
        Promise.resolve(dataProvider(index))
            .then((data) => {
                if (mod !== renderer.mod) return // another update has been called.
                internalUpdate++
                const dataC = renderer.data$
                if (dataC.value === data) {
                    if (forceInvalidate) dataC.invalidate()
                } else {
                    renderer.data$.value = data
                }
                internalUpdate--
            })
            .catch(errorHandler)
    }

    const pool = new IndexedPool(createRenderer)

    let scrollParent: Element | null = null

    const sizer = createRenderer()
    {
        sizer.show()
        clearRenderer(sizer, 0)
        const e = sizer.element
        e.style.visibility = 'hidden'
        e.style.pointerEvents = 'none'
        view.append(e)
    }
    let sizerBounds: DOMRect | null = null
    let zoomInv = 1

    function render() {
        if (!view.isConnected) return
        const n = countData.value
        if (n == null) return

        if (!sizerBounds) {
            sizerBounds = sizer.element.getBoundingClientRect()
            zoomInv = sizer.element.offsetHeight / sizerBounds.height
        }
        if (!sizerBounds.height || !sizerBounds.width) return
        const rendererSize = sizerBounds.height
        view.style.height = countData.value ? `${countData.value * rendererSize * zoomInv}px` : '0'

        const visibleBounds = getVisibleRect(view, scrollParent)
        if (!visibleBounds) return

        const margin = props.margin ?? DEFAULT_MARGIN

        const startPx = visibleBounds.top - margin
        const endPx = visibleBounds.bottom + margin

        const startIndex = Math.floor(startPx / rendererSize)
        const clampedStartIndex = Math.max(0, startIndex)
        const endIndex = Math.ceil(endPx / rendererSize)
        const clampedEndIndex = Math.min(n, endIndex)
        let needsNewData = false

        for (let i = startIndex; i < endIndex; i++) {
            const renderer = pool.obtain(i)
            if (i < clampedStartIndex || i >= clampedEndIndex) {
                // Obtain virtual elements beyond the bounds we're rendering so that
                // recycling pulls from these hidden renderers before taking from the visible ones.
                renderer.hide()
                continue
            }
            const indexChanged = renderer.index$.value !== i
            if (indexChanged) {
                needsNewData = true
                clearRenderer(renderer, i)
                // transform does not trigger layout recalculation — it's just GPU layer positioning.
                renderer.element.style.transform = `translateY(${i * rendererSize * zoomInv}px)`
            }
            renderer.show()
        }

        pool.forEachUnused((_, unusedRenderer) => {
            unusedRenderer.hide()
        })
        pool.flip()

        needsReordering = true
        reorderElementsThrottled()
        if (needsNewData) updateDataThrottled()
    }

    let needsReordering = true
    function reorderElements() {
        if (!needsReordering) return
        needsReordering = false
        const activeElement = document.activeElement as HTMLElement | null
        let previousElement: Element | null = null
        pool.forEachUnused((_, renderer) => {
            insertAfter(view, renderer.element, previousElement)
            previousElement = renderer.element
        })
        if (document.activeElement !== activeElement) {
            activeElement?.focus()
        }
    }

    const updateDataThrottled = throttle(
        () => {
            pool.forEachUnused((index, renderer) => {
                if (renderer.data$.value === null) refreshRendererData(renderer, index, false)
            })
        },
        props.dataProviderThrottle ?? 0,
        {
            leading: true,
            trailing: true,
        }
    )

    const reorderElementsThrottled = debounce(reorderElements, 0.2)

    let needsRender = false

    function queueRender() {
        if (needsRender) return
        needsRender = true
        requestAnimationFrame(() => {
            needsRender = false
            render()
        })
    }

    let useControllerSub: Unsubscribe | null = null

    onConnect(view, () => {
        const { add, dispose } = createDisposer()
        scrollParent = getScrollParent(view, direction)
        const passiveOpts = { passive: true }
        scrollParent?.addEventListener('scroll', queueRender, passiveOpts)
        add(() => scrollParent?.removeEventListener('scroll', queueRender))
        add(windowEvents.on('scroll', queueRender, passiveOpts))
        add(countData.onData(queueRender))
        add(windowEvents.on('resize', queueRender))
        add(windowEvents.on('orientationchange', queueRender))
        add(windowEvents.on('keydown', reorderElements, { capture: true }))
        add(
            onResize(sizer.element, () => {
                // Clear the cached sizer bounds
                sizerBounds = null
                queueRender()
            })
        )

        if (props.useController) {
            useControllerSub = props.useController(controller)
            // Listen for invalidate events
            add(
                controller.on('invalidated', (event) => {
                    pool.forEachUnused((index, renderer) => {
                        if (index >= event.startIndex && index < event.endIndex) {
                            refreshRendererData(renderer, index, true)
                        }
                    })
                })
            )
        }

        return () => {
            scrollParent = null
            dispose()
            useControllerSub?.()
            useControllerSub = null
        }
    })

    return view
}

/**
 * Returns the visible portion of an element's bounding box,
 * accounting for scroll clipping from one provided ancestor element and the viewport.
 *
 * @param element - The element whose visible bounding rect is to be computed.
 * @param scrollParent - The
 * @returns A DOMRect-like object representing the visible part of the element,
 *          or null if it is fully clipped. This will be in coordinates relative to element.
 */
function getVisibleRect(element: Element, scrollParent: Element | null): DOMRect | null {
    const baseRect = element.getBoundingClientRect()
    let visibleRect: DOMRect | null = baseRect

    if (scrollParent) {
        const clipRect = scrollParent.getBoundingClientRect()
        visibleRect = intersectRects(visibleRect, clipRect)
        if (!visibleRect) return null
    }
    visibleRect = intersectRects(visibleRect, getViewportRect())
    if (!visibleRect) return null
    return toLocalRect(visibleRect, baseRect)
}

/**
 * Inserts `newNode` after `referenceNode` in the DOM.
 * If `referenceNode` is nullish, inserts at the beginning of the parent's children.
 *
 * @param parent - The parent container.
 * @param newNode - The node to insert.
 * @param referenceNode - The node after which to insert, or null to insert at the start.
 */
function insertAfter(parent: ParentNode, newNode: Node, referenceNode?: Maybe<Node>) {
    const next = referenceNode?.nextSibling ?? parent.firstElementChild

    if (next === newNode) return // Already in position
    parent.insertBefore(newNode, next)
}
